import React, { useEffect, useState } from "react";
import { Col, Table, Spinner, Badge, Row, Container, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";
import { FaArrowLeft, FaPrint, FaGlobe, FaShip, FaFileContract } from "react-icons/fa";

const SalesInternationalView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [salesData, setSalesData] = useState(null);

    const fetchSalesData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}sales/findById/${id}`, {
                headers: authHeader()
            });
            setSalesData(response.data);
        } catch (error) {
            console.error("Error fetching sales data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch sales data. Please try again.",
            });
            navigate("/salesIndexTable/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0.00';
        return parseFloat(value).toFixed(2);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Draft': return 'secondary';
            case 'Submitted': return 'primary';
            case 'Approved': return 'success';
            case 'Rejected': return 'danger';
            case 'Completed': return 'info';
            case 'Open': return 'warning';
            default: return 'light';
        }
    };

    const getAddressString = (address) => {
        if (!address) return '-';
        return [
            address.customeraddress,
            address.city?.cityname,
            address.state?.statename,
            address.pincode,
            address.country?.countryname
        ].filter(Boolean).join(', ');
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!salesData) {
        return <div className="alert alert-danger text-center mt-4">Sales invoice not found</div>;
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">INTERNATIONAL SALES INVOICE - VIEW</h5>
                        <div>
                            <Button variant="outline-primary" size="sm" className="me-2"
                                onClick={() => navigate(`/salesInvoiceManufacturingInternational/print/${id}`)}
                            >
                                <FaPrint className="me-1" />
                                Print
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </Button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Invoice No</small>
                                <div className="fw-bold">{salesData.invoiceno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Invoice Date</small>
                                <div className="fw-bold">{formatDate(salesData.invoicedate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Currency</small>
                                <div className="fw-bold">{salesData.salescurrency || 'USD'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge bg={getStatusBadge(salesData.status)} className="px-2 py-1">
                                        {salesData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Invoice Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold"><FaFileContract className="me-2" size={14} />Invoice Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Tally Reference No</small>
                            <div className="fw-bold">{salesData.tallyreferenceno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Dispatch Through</small>
                            <div className="fw-bold">{salesData.dispatchthrough || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Exchange Rate (₹/Currency)</small>
                            <div className="fw-bold">{formatNumber(salesData.salesexchangerate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Branch</small>
                            <div className="fw-bold">{salesData.branch?.branchname || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Warehouse</small>
                            <div className="fw-bold">{salesData.warehouse?.warehousename || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Reference</small>
                            <div className="fw-bold">{salesData.reference || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* International Details */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold"><FaGlobe className="me-2" size={14} />International Details</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Kind Attention</small>
                            <div className="fw-bold">{salesData.kindattn || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Country of Destination</small>
                            <div className="fw-bold">{salesData.countryofdestination || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Country of Origin</small>
                            <div className="fw-bold">{salesData.countryoforigin || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Air/Sea</small>
                            <div className="fw-bold">{salesData.airsea || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Port of Loading</small>
                            <div className="fw-bold">{salesData.portofloading || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Port of Discharge</small>
                            <div className="fw-bold">{salesData.portofdischarge || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Shipping Details */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold"><FaShip className="me-2" size={14} />Shipping Details</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">No. of Boxes</small>
                            <div className="fw-bold">{salesData.noofboxes || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Net Weight (kg)</small>
                            <div className="fw-bold">{salesData.netweightofboxes || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Gross Weight (kg)</small>
                            <div className="fw-bold">{salesData.grossweightofboxes || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Customs & Regulatory Details */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Customs & Regulatory Details</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">LUT Bond No</small>
                            <div className="fw-bold">{salesData.lutbondno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">LUT Bond Date</small>
                            <div className="fw-bold">{formatDate(salesData.lutbonddate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Type of Shipping Bill</small>
                            <div className="fw-bold">{salesData.typeofshippingbill || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Drawback A/C No</small>
                            <div className="fw-bold">{salesData.drawbackacno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Chapter Heading</small>
                            <div className="fw-bold">{salesData.chapterheading || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Tariff Item</small>
                            <div className="fw-bold">{salesData.tariffitem || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Drawback Rate</small>
                            <div className="fw-bold">{salesData.drawbackrate || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Customer Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Customer Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Company Name</small>
                            <div className="fw-bold">{salesData.customer?.companyname || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Contact Person</small>
                            <div className="fw-bold">{salesData.contactperson || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">GST No</small>
                            <div className="fw-bold">{salesData.customer?.gstno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Phone</small>
                            <div className="fw-bold">{salesData.contactno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Email</small>
                            <div className="fw-bold">{salesData.email || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Payment Terms</small>
                            <div className="fw-bold">{salesData.customer?.paymentterm || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Incoterms</small>
                            <div className="fw-bold">{salesData.customer?.incoterms || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Bill To and Ship To */}
            <Row className="mb-3">
                <Col md={6}>
                    <div className="shadow-sm border rounded bg-white h-100">
                        <div className="bg-light py-2 px-3 border-bottom">
                            <h6 className="mb-0 fw-bold">Bill To</h6>
                        </div>
                        <div className="p-3">
                            {salesData.billto ? (
                                <>
                                    <small className="text-muted">Address</small>
                                    <div className="fw-bold mb-2">{getAddressString(salesData.billto)}</div>
                                    <small className="text-muted">GST</small>
                                    <div className="fw-bold">{salesData.billto.gstno || '-'}</div>
                                </>
                            ) : (
                                <div className="text-muted">No billing address specified</div>
                            )}
                        </div>
                    </div>
                </Col>
                <Col md={6}>
                    <div className="shadow-sm border rounded bg-white h-100">
                        <div className="bg-light py-2 px-3 border-bottom">
                            <h6 className="mb-0 fw-bold">Ship To</h6>
                        </div>
                        <div className="p-3">
                            {salesData.shipto ? (
                                <>
                                    <small className="text-muted">Address</small>
                                    <div className="fw-bold mb-2">{getAddressString(salesData.shipto)}</div>
                                    <small className="text-muted">Contact</small>
                                    <div className="fw-bold mb-2">{salesData.shipto.contactperson || '-'}</div>
                                    <small className="text-muted">Phone</small>
                                    <div className="fw-bold">{salesData.shipto.contactno || '-'}</div>
                                </>
                            ) : (
                                <div className="text-muted">No shipping address specified</div>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Additional Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Additional Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <small className="text-muted">Description</small>
                            <div className="p-2 rounded mt-1" style={{ backgroundColor: '#f8f9fa' }}>
                                <span className="fw-bold">{salesData.description || '-'}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">LUT Details</small>
                            <div className="p-2 rounded mt-1" style={{ backgroundColor: '#f8f9fa' }}>
                                <span className="fw-bold">{salesData.lut || '-'}</span>
                            </div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">From Date</small>
                            <div className="fw-bold">{formatDate(salesData.fromdate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">To Date</small>
                            <div className="fw-bold">{formatDate(salesData.todate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Destination</small>
                            <div className="fw-bold">{salesData.destination || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Products Table */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Products</h6>
                </div>
                <div className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Item Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate (FC)</th>
                                    <th className="fw-bold py-2 text-end">Total (FC)</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate (₹)</th>
                                    <th className="fw-bold py-2 text-end">Total (₹)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {salesData.salesItems?.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{item.product?.description || '-'}</div>
                                            {item.productremarks && item.productremarks !== '-' && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {item.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{item.partno || '-'}</td>
                                        <td>{item.hsnsacno || '-'}</td>
                                        <td>{item.serialnumber || '-'}</td>
                                        <td>{item.modelnumber || '-'}</td>
                                        <td>{item.tagnumber || '-'}</td>
                                        <td>{item.product?.moc || item.moc || '-'}</td>
                                        <td>{item.itemcode || '-'}</td>
                                        <td className="text-center">{item.quantity || '0'}</td>
                                        <td className="text-center">{item.uom || '-'}</td>
                                        <td className="text-end">{formatNumber(item.unitratecurrency)}</td>
                                        <td className="text-end fw-semibold">{formatNumber(item.totalcurrency)}</td>
                                        <td className="text-end">{formatCurrency(item.unitrateinr)}</td>
                                        <td className="text-end fw-semibold">{formatCurrency(item.totalinr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Calculation Summary */}
            <Row className="mb-3">
                <Col lg={6} className="ms-auto">
                    <div className="shadow-sm border rounded bg-white">
                        <div className="bg-light py-2 px-3 border-bottom">
                            <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
                        </div>
                        <div className="p-3">
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Net Total (INR):</span>
                                <span className="fw-bold">{formatCurrency(salesData.nettotalinr)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Net Total ({salesData.salescurrency || 'USD'}):</span>
                                <span className="fw-bold">{formatNumber(salesData.nettotalcurrency)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Export Charges ({salesData.salescurrency || 'USD'}):</span>
                                <span className="fw-bold">{formatNumber(salesData.exportpreperationchargescurrency)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Export Charges (INR):</span>
                                <span className="fw-bold">
                                    {formatCurrency(parseFloat(salesData.exportpreperationchargescurrency || 0) * parseFloat(salesData.salesexchangerate || 1))}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                <span className="fw-bold">Grand Total (INR)</span>
                                <span className="fw-bold text-primary">{formatCurrency(salesData.grandtotalinr)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2">
                                <span className="fw-bold">Grand Total ({salesData.salescurrency || 'USD'})</span>
                                <span className="fw-bold text-success">{formatNumber(salesData.grandtotalcurrency)}</span>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Button>
            </div>
        </Container>
    );
};

export default SalesInternationalView;